import React, { useState } from "react";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Avatar from "../../components/ui/Avatar";
import Badge from "../../components/ui/Badge";
import { Camera, Check } from "../../utils/icons";

const tabs = ["Profile", "Security", "Subscription", "Notifications"];

const Settings = () => {
  const [activeTab, setActiveTab] = useState("Profile");
  const [saved, setSaved] = useState(false);
  const [profile, setProfile] = useState({
    name: "Aria Chen",
    email: "aria@techcorp.io",
    title: "Engineering Lead",
    bio: "Building the future, one deploy at a time.",
    company: "TechCorp",
  });
  const [passwords, setPasswords] = useState({
    current: "",
    next: "",
    confirm: "",
  });
  const [notifications, setNotifications] = useState({
    email: true,
    slack: false,
    billing: true,
    security: true,
    product: false,
  });

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-display text-2xl font-bold text-gray-900 dark:text-white">
          Settings
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Manage your account and preferences
        </p>
      </div>

      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1 mb-6 w-full sm:w-auto sm:inline-flex">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white" : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"}`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === "Profile" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="p-6 flex flex-col items-center text-center">
            <div className="relative mb-4">
              <Avatar name={profile.name} size="xl" />
              <button className="absolute bottom-0 right-0 w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center hover:bg-primary-700 transition-colors">
                <Camera size={14} className="text-white" />
              </button>
            </div>
            <h3 className="font-display font-bold text-gray-900 dark:text-white">
              {profile.name}
            </h3>
            <p className="text-gray-500 text-sm">{profile.title}</p>
            <Badge color="primary" className="mt-2">
              Admin
            </Badge>
          </Card>

          <Card className="lg:col-span-2 p-6">
            <h3 className="font-display font-bold text-gray-900 dark:text-white mb-5">
              Personal Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { label: "Full Name", key: "name" },
                { label: "Company", key: "company" },
                { label: "Email Address", key: "email" },
                { label: "Job Title", key: "title" },
              ].map(({ label, key }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    {label}
                  </label>
                  <input
                    type="text"
                    value={profile[key]}
                    onChange={(e) =>
                      setProfile({ ...profile, [key]: e.target.value })
                    }
                    className="input-field"
                  />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Bio
                </label>
                <textarea
                  value={profile.bio}
                  onChange={(e) =>
                    setProfile({ ...profile, bio: e.target.value })
                  }
                  rows={3}
                  className="input-field resize-none"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <Button
                onClick={handleSave}
                icon={saved ? <Check size={16} /> : null}
                variant={saved ? "subtle" : "primary"}
              >
                {saved ? "Saved!" : "Save Changes"}
              </Button>
              <Button variant="ghost">Discard</Button>
            </div>
          </Card>
        </div>
      )}

      {activeTab === "Security" && (
        <div className="max-w-xl space-y-4">
          <Card className="p-6">
            <h3 className="font-display font-bold text-gray-900 dark:text-white mb-5">
              Change Password
            </h3>
            <div className="space-y-4">
              {[
                { label: "Current Password", key: "current" },
                { label: "New Password", key: "next" },
                { label: "Confirm New Password", key: "confirm" },
              ].map(({ label, key }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    {label}
                  </label>
                  <input
                    type="password"
                    value={passwords[key]}
                    onChange={(e) =>
                      setPasswords({ ...passwords, [key]: e.target.value })
                    }
                    className="input-field"
                    placeholder="••••••••"
                  />
                </div>
              ))}
              <Button onClick={handleSave}>Update Password</Button>
            </div>
          </Card>
          <Card className="p-6">
            <h3 className="font-display font-bold text-gray-900 dark:text-white mb-1">
              Two-Factor Authentication
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Add an extra layer of protection to your account.
            </p>
            <Button variant="outline">Enable 2FA</Button>
          </Card>
          <Card className="p-6 border-red-200 dark:border-red-900/30">
            <h3 className="font-display font-bold text-red-600 mb-1">
              Danger Zone
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Permanently delete your account and all associated data.
            </p>
            <Button variant="danger">Delete Account</Button>
          </Card>
        </div>
      )}

      {activeTab === "Subscription" && (
        <div className="max-w-2xl space-y-4">
          <Card className="p-6">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <h3 className="font-display font-bold text-gray-900 dark:text-white mb-1">
                  Pro Plan
                </h3>
                <p className="text-gray-500 text-sm">
                  $79/month · Renews June 1, 2024
                </p>
              </div>
              <Badge color="primary" size="md">
                Active
              </Badge>
            </div>
            <div className="mt-5 pt-5 border-t border-gray-100 dark:border-gray-800 grid grid-cols-2 gap-3">
              <Button variant="outline">Upgrade to Enterprise</Button>
              <Button
                variant="ghost"
                className="!text-red-500 !border-red-200 dark:!border-red-900/30 hover:!bg-red-50 dark:hover:!bg-red-900/20"
              >
                Cancel Plan
              </Button>
            </div>
          </Card>
          <Card className="p-6">
            <h3 className="font-display font-bold text-gray-900 dark:text-white mb-4">
              Usage
            </h3>
            {[
              { label: "API Calls", used: 7842, total: 10000 },
              { label: "Storage", used: 47, total: 100 },
              { label: "Team Members", used: 12, total: 25 },
            ].map((item) => (
              <div key={item.label} className="mb-4">
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-gray-600 dark:text-gray-400">
                    {item.label}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {item.used.toLocaleString()} / {item.total.toLocaleString()}
                  </span>
                </div>
                <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${item.used / item.total > 0.8 ? "bg-red-500" : "bg-gradient-to-r from-primary-500 to-accent-500"}`}
                    style={{ width: `${(item.used / item.total) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </Card>
        </div>
      )}

      {activeTab === "Notifications" && (
        <div className="max-w-xl">
          <Card className="p-6">
            <h3 className="font-display font-bold text-gray-900 dark:text-white mb-5">
              Notification Preferences
            </h3>
            <div className="space-y-5">
              {[
                {
                  key: "email",
                  label: "Email Notifications",
                  desc: "Receive important updates via email",
                },
                {
                  key: "slack",
                  label: "Slack Notifications",
                  desc: "Push alerts to your Slack workspace",
                },
                {
                  key: "billing",
                  label: "Billing Alerts",
                  desc: "Payment receipts and subscription reminders",
                },
                {
                  key: "security",
                  label: "Security Alerts",
                  desc: "Login attempts and account changes",
                },
                {
                  key: "product",
                  label: "Product Updates",
                  desc: "New features, changelogs, and tips",
                },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {label}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                  </div>
                  <button
                    onClick={() =>
                      setNotifications({
                        ...notifications,
                        [key]: !notifications[key],
                      })
                    }
                    className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${notifications[key] ? "bg-primary-600" : "bg-gray-200 dark:bg-gray-700"}`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${notifications[key] ? "translate-x-5" : ""}`}
                    />
                  </button>
                </div>
              ))}
            </div>
            <Button onClick={handleSave} className="mt-6">
              {saved ? "Saved!" : "Save Preferences"}
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Settings;
